import React from 'react';
import { Controller } from 'react-hook-form';
import type { ControllerRenderProps } from 'react-hook-form';
import get from 'lodash/get';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Checkbox from 'components/common/input-components/checkbox';
import { DesktopText } from 'components/common/form-components';
import { setLink } from 'components/common/link';
import { links } from 'constants/external-links';
import { showHiddenFeatures } from 'constants/config';
import styled from 'types/theme.types';
import type { CredentialsInputProps } from 'components/organizations/credentials/credential-modal';
import type { CredentialType, OidcTokenFormat } from 'types/api.types';
import OidcClaimField from 'components/organizations/credentials/oidc-claim-field';
import { getOidcAudience } from 'components/organizations/credentials/oidc-audiences';

type OidcV2FieldsProps = {
  form: CredentialsInputProps['form'];
  credentialType: CredentialType;
};

const OidcV2Fields: React.FunctionComponent<OidcV2FieldsProps> = ({ form, credentialType }) => {
  if (!showHiddenFeatures()) return null;

  return (
    <Controller
      name={'value.tokenFormat' as never}
      control={form.control}
      defaultValue={get(form.getValues() as Record<string, unknown>, 'value.tokenFormat') as never}
      render={({ value, onChange }: ControllerRenderProps) => {
        const tokenFormat = value as OidcTokenFormat | undefined;
        const onToggle = (e: CheckboxChangeEvent): void => onChange(e.target.checked ? 'v2' : undefined);

        return (
          <>
            <CheckboxRow>
              <Checkbox checked={tokenFormat === 'v2'} onChange={onToggle} data-e2e="oidc-use-v2-token">
                <DesktopText
                  id="settings.credentials.oidc.use-v2-token"
                  info={{
                    id: 'settings.credentials.oidc.use-v2-token.info',
                    values: setLink(links.docs.DEPLOYMENT_CREDENTIALS.OIDC_V2_TOKEN_DOCS)
                  }}
                />
              </Checkbox>
            </CheckboxRow>
            <OidcClaimField
              labelId="settings.credentials.oidc.audience"
              value={getOidcAudience(credentialType, tokenFormat)}
              dataE2e="oidc-audience"
            />
          </>
        );
      }}
    />
  );
};

const CheckboxRow = styled.div`
  margin: 4px 0 19px;
`;

export default OidcV2Fields;
